import { DateTime } from "luxon";
import { cn } from "@/lib/utils";
import { UseFormReturn } from "react-hook-form";
import { Checkbox } from "@/components/ui/checkbox";
import { Combobox } from "@/components/ui/combobox";
import { DatePicker } from "@/components/ui/date-picker";
import { Input } from "@/components/ui/input";
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Textarea } from "@/components/ui/textarea";
import { UserType } from "@/types/user";
import { ApprenticeshipProductType } from "@/types/apprenticeship/product";
import { ApprenticeshipOfferType } from "@/types/apprenticeship/enterprise";
import { OfferFormData } from "./_schema";

interface Props {
    form: UseFormReturn<OfferFormData>;
    is_cap: boolean;
    is_admin: boolean;
    offer?: ApprenticeshipOfferType;
    user: UserType | null;
    /** SF surfaces use stage (Deprecated / New); hide on account create + opportunity edit. Legacy offer dialogs keep the checkbox. */
    showActiveToggle?: boolean;
    /** Account create and opportunity edit only. The legacy offer dialog does not pass this. */
    showAvailableSeats?: boolean;
}

export function OfferCommonForm({ form, is_cap, is_admin, offer, user, showActiveToggle = true, showAvailableSeats = false }: Props) {
    const handleCheckFindBy = (field: "found_by_candidate" | "found_by_csm", value: boolean) => {
        if (value) {
            if (field === "found_by_candidate") {
                form.setValue("found_by_csm", false);
            } else {
                form.setValue("found_by_candidate", false);
            }
        }
        form.setValue(field, value);
    };

    return (
        <div className="space-y-4">
            {!form.watch("pro_a") && (
                <div className="w-full flex flex-col sm:flex-row gap-4 items-stretch">
                    <FormField
                        control={form.control}
                        name="apprentissage"
                        render={({ field }) => (
                            <FormItem className="w-full sm:w-1/2 flex flex-row items-start space-x-3 space-y-0 rounded-md border p-4">
                                <FormControl>
                                    <Checkbox
                                        checked={field.value}
                                        onCheckedChange={field.onChange}
                                    />
                                </FormControl>
                                <div className="space-y-1 leading-none">
                                    <FormLabel>Accepte les contrats d'apprentissage</FormLabel>
                                </div>
                            </FormItem>
                        )}
                    />
                    <FormField
                        control={form.control}
                        name="contrat_pro"
                        render={({ field }) => (
                            <FormItem className="w-full sm:w-1/2 flex flex-row items-start space-x-3 space-y-0 rounded-md border p-4">
                                <FormControl>
                                    <Checkbox
                                        checked={field.value}
                                        onCheckedChange={field.onChange}
                                    />
                                </FormControl>
                                <div className="space-y-1 leading-none">
                                    <FormLabel>Accepte les contrats pro</FormLabel>
                                </div>
                            </FormItem>
                        )}
                    />
                </div>
            )}

            <div className="w-full flex flex-col sm:flex-row gap-4 items-stretch">
                <FormField
                    control={form.control}
                    name="product"
                    render={({ field }) => (
                        <FormItem className="w-full sm:w-1/2 lg:w-1/3">
                            <FormLabel>Parcours de formation associé *</FormLabel>
                            <FormControl>
                                <div>
                                    <Combobox
                                        name={field.name}
                                        placeholder="Sélectionner un parcours de formation"
                                        optionsUrl="/alternance/picklists/products"
                                        optionFormatter={(product: ApprenticeshipProductType) => {
                                            return { 
                                                value: product.id.toString(),
                                                label: product.short_name || product.name || product.product_code || product.id.toString() 
                                            };
                                        }}
                                        defaultValue={field.value}
                                        onValueChange={field.onChange}
                                        className="w-full"
                                    />
                                </div>
                            </FormControl>
                            <FormMessage />
                        </FormItem>
                    )}
                />
                <FormField
                    control={form.control}
                    name="desired_starting_date"
                    render={({ field }) => (
                        <FormItem className="w-full sm:w-1/2 lg:w-1/3">
                            <FormLabel>Date de début d'alternance souhaitée</FormLabel>
                            <FormControl>
                                <div>
                                    <DatePicker
                                        date={field.value ? DateTime.fromISO(field.value) : undefined}
                                        setDate={d => field.onChange(d ? d.toISODate() : null)}
                                        className="w-full"
                                    />
                                </div>
                            </FormControl>
                            <FormMessage />
                        </FormItem>
                    )}
                />
                <FormField
                    control={form.control}
                    name="job_link"
                    render={({ field }) => (
                        <FormItem className="w-full lg:w-1/3">
                            <FormLabel>Lien vers l'offre</FormLabel>
                            <FormControl>
                                <Input placeholder="https://www.linkedin.com/job-offers/..." {...field} />
                            </FormControl>
                            <FormMessage />
                        </FormItem>
                    )}
                />
            </div>
            {showAvailableSeats && (
                <FormField
                    control={form.control}
                    name="available_seats"
                    render={({ field }) => (
                        <FormItem className="w-full sm:w-1/2 lg:w-1/3">
                            <FormLabel>Places disponibles *</FormLabel>
                            <FormControl>
                                <Input
                                    {...field}
                                    type="number"
                                    min={1}
                                    step={1}
                                    value={field.value ?? ""}
                                    placeholder="1"
                                    onChange={e => {
                                        const raw = e.target.value;
                                        if (raw === "") {
                                            field.onChange(undefined);
                                            return;
                                        }
                                        const v = Number.parseInt(raw, 10);
                                        if (!Number.isNaN(v)) {
                                            field.onChange(v);
                                        }
                                    }}
                                />
                            </FormControl>
                            <FormMessage />
                        </FormItem>
                    )}
                />
            )}
            <FormField
                control={form.control}
                name="interview_slots_description"
                render={({ field }) => (
                    <FormItem className="w-full">
                        <FormLabel>Disponibilité pour les entretiens</FormLabel>
                        <FormControl>
                            <Textarea placeholder="Mardi 14h-17h, Jeudi 10h-12h, ..." {...field} />
                        </FormControl>
                        <FormMessage />
                    </FormItem>
                )}
            />

            <div className="w-full flex flex-row gap-4">
                {is_cap && (
                    <FormField
                        control={form.control}
                        name="found_by_candidate"
                        render={({ field }) => (
                            <FormItem className="w-full sm:w-1/2 flex flex-row items-start space-x-3 space-y-0 rounded-md border p-4">
                                <FormControl>
                                    <Checkbox
                                        checked={field.value}
                                        onCheckedChange={(value) => handleCheckFindBy("found_by_candidate", value as boolean)}
                                    />
                                </FormControl>
                                <div className="space-y-1 leading-none">
                                    <FormLabel>Cette offre a été trouvée par un candidat</FormLabel>
                                </div>
                            </FormItem>
                        )}
                    />
                )}
                {is_admin && (
                    <FormField
                        control={form.control}
                        name="found_by_csm"
                        render={({ field }) => (
                            <FormItem className="w-full sm:w-1/2 flex flex-row items-start space-x-3 space-y-0 rounded-md border p-4">
                                <FormControl>
                                    <Checkbox 
                                        checked={field.value}
                                        onCheckedChange={(value) => handleCheckFindBy("found_by_csm", value as boolean)}
                                    />
                                </FormControl>
                                <div className="space-y-1 leading-none">
                                    <FormLabel>Cette offre a été trouvée par un CSM</FormLabel>
                                </div>
                            </FormItem>
                        )}
                    />
                )}
                {/* User can only manage their offer and only admins can reactivate an offer.
                    On SF opportunities, activate/deactivate is stage-driven (New / Deprecated). */}
                {showActiveToggle &&
                    (is_admin || (offer?.active && offer?.owner?.id && offer.owner.id == user?.id)) && (
                    <FormField
                        control={form.control}
                        name="active"
                        render={({ field }) => (
                            <FormItem className="w-full sm:w-1/2 flex flex-row items-start space-x-3 space-y-0 rounded-md border p-4">
                                <FormControl>
                                    <Checkbox
                                        checked={field.value}
                                        onCheckedChange={field.onChange}
                                    />
                                </FormControl>
                                <div className="space-y-1 leading-none">
                                    <FormLabel>Activer l'offre</FormLabel>
                                </div>
                            </FormItem>
                        )}
                    />
                )}

                <FormField
                    control={form.control}
                    name="rqth"
                    render={({ field }) => (
                        <FormItem className="w-full sm:w-1/2 flex flex-row items-center gap-3 space-y-0 rounded-md border p-4">
                            <FormControl>
                                <Checkbox checked={field.value} onCheckedChange={field.onChange} />
                            </FormControl>
                            <div className="space-y-1 leading-none">
                                <FormLabel>L'entreprise recherche un profil RQTH</FormLabel>
                            </div>
                        </FormItem>
                    )}
                />
            </div>

            <div className="w-full grid gap-4 grid-cols-2">
                {!form.watch("pro_a") && (
                        <div className="grid grid-cols-2 gap-4 lg:col-span-4">
                            <div className="space-y-4 rounded-sm border p-4">
                                <div>
                                    <FormField
                                        control={form.control}
                                        name="age_max_toggle"
                                        render={({ field }) => (
                                            <FormItem className="flex flex-row items-start space-x-3 space-y-0 ">
                                                <FormControl>
                                                    <Checkbox
                                                        checked={field.value}
                                                        onCheckedChange={value => {
                                                            field.onChange(value);
                                                            if (!value) {
                                                                form.setValue("age_max", null);
                                                            }
                                                        }}
                                                    />
                                                </FormControl>
                                                <div className="space-y-1 leading-none">
                                                    <FormLabel>Age maximal du candidat</FormLabel>
                                                </div>
                                            </FormItem>
                                        )}
                                    />
                                </div>
                                <div className={cn(form.watch("age_max_toggle") ? "opacity-100" : "opacity-50")}>
                                    <FormField
                                        control={form.control}
                                        name="age_max"
                                        disabled={!form.watch("age_max_toggle")}
                                        render={({ field }) => (
                                            <FormItem>
                                                <FormControl>
                                                    <Input
                                                        {...field}
                                                        value={field.value || ""}
                                                        placeholder="Indiquer l'age maximal"
                                                        onChange={e => {
                                                            try {
                                                                if (e.target.value.length > 0) {
                                                                    const v = parseFloat(e.target.value);
                                                                    if (!Number.isNaN(v)) {
                                                                        field.onChange(v);
                                                                    }
                                                                } else {
                                                                    field.onChange(0);
                                                                }
                                                            } catch (e) {}
                                                        }}
                                                    />
                                                </FormControl>
                                                <FormMessage />
                                            </FormItem>
                                        )}
                                    />
                                </div>
                            </div>

                            <div className="space-y-4 rounded-sm border p-4">
                                <div>
                                    <FormField
                                        control={form.control}
                                        name="max_commute_toggle"
                                        render={({ field }) => (
                                            <FormItem className="flex flex-row items-start space-x-3 space-y-0 ">
                                                <FormControl>
                                                    <Checkbox
                                                        checked={field.value}
                                                        onCheckedChange={value => {
                                                            field.onChange(value);
                                                            if (!value) {
                                                                form.setValue("max_commute", null);
                                                            }
                                                        }}
                                                    />
                                                </FormControl>
                                                <div className="space-y-1 leading-none">
                                                    <FormLabel>Durée de trajet maximum</FormLabel>
                                                </div>
                                            </FormItem>
                                        )}
                                    />
                                </div>
                                <div className={cn(form.watch("max_commute_toggle") ? "opacity-100" : "opacity-50")}>
                                    <FormField
                                        control={form.control}
                                        name="max_commute"
                                        disabled={!form.watch("max_commute_toggle")}
                                        render={({ field }) => (
                                            <FormItem>
                                                <FormControl>
                                                    <Input
                                                        {...field}
                                                        value={field.value || ""}
                                                        placeholder="Indiquer la durée en minutes"
                                                        onChange={e => {
                                                            try {
                                                                if (e.target.value.length > 0) {
                                                                    const v = parseFloat(e.target.value);
                                                                    if (!Number.isNaN(v)) {
                                                                        field.onChange(v);
                                                                    }
                                                                } else {
                                                                    field.onChange(0);
                                                                }
                                                            } catch (e) {}
                                                        }}
                                                    />
                                                </FormControl>
                                                <FormMessage />
                                            </FormItem>
                                        )}
                                    />
                                </div>
                            </div>
                        </div>
                    )}
            </div>
        </div>
    );
}
